import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Zap, Mail, Lock, User, ArrowRight, Wifi, WifiOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('unknown'); // 'unknown' | 'checking' | 'connected' | 'disconnected'
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const checkConnection = useCallback(async () => {
    setConnectionStatus('checking');
    try {
      const res = await fetch(`${API}/api/health`, { signal: AbortSignal.timeout(5000) });
      if (res.ok) {
        const data = await res.json();
        setConnectionStatus(data.database === 'connected' ? 'connected' : 'disconnected');
      } else {
        setConnectionStatus('disconnected');
      }
    } catch {
      setConnectionStatus('disconnected');
    }
  }, []);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password);
      } else {
        await login(email, password);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="login-page">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1627280052756-cc5e080c8458?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505]" />
        <div className="relative z-10 px-16 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg bg-red-600 flex items-center justify-center">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-wider text-white uppercase">My ETA</h1>
          </div>
          <h2 className="font-heading text-4xl font-bold text-white uppercase tracking-tight leading-tight mb-4">
            AI-Powered Lead<br />Response Engine
          </h2>
        </div>
      </div>

      {/* Right panel - Auth form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading text-xl font-bold tracking-wider text-white uppercase">My ETA</h1>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight">
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-slate-400 text-sm mt-2">
              {isRegister ? 'Set up your dealership command center' : 'Sign in to your lead command center'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Name</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="John Smith"
                    className="pl-10 bg-[#111] border-[#333] focus:ring-1 focus:ring-red-600 focus:border-red-600 h-11 text-sm placeholder:text-slate-600"
                    data-testid="register-name-input"
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@dealership.com"
                  className="pl-10 bg-[#111] border-[#333] focus:ring-1 focus:ring-red-600 focus:border-red-600 h-11 text-sm placeholder:text-slate-600"
                  data-testid="email-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 bg-[#111] border-[#333] focus:ring-1 focus:ring-red-600 focus:border-red-600 h-11 text-sm placeholder:text-slate-600"
                  data-testid="password-input"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-md px-4 py-2" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase transition-all hover:shadow-[0_0_15px_rgba(220,38,38,0.4)]"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {isRegister ? 'Create Account' : 'Sign In'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            {/* Connection Status Indicator */}
            <div className="flex items-center justify-between mt-3 px-1" data-testid="connection-status-area">
              <div className="flex items-center gap-2">
                {connectionStatus === 'connected' ? (
                  <Wifi className="w-3.5 h-3.5 text-emerald-400" />
                ) : connectionStatus === 'disconnected' ? (
                  <WifiOff className="w-3.5 h-3.5 text-red-400" />
                ) : (
                  <Wifi className="w-3.5 h-3.5 text-slate-500" />
                )}
                <div className="flex items-center gap-1.5">
                  <span
                    data-testid="connection-status-dot"
                    className={`w-2 h-2 rounded-full ${
                      connectionStatus === 'connected'
                        ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]'
                        : connectionStatus === 'disconnected'
                        ? 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]'
                        : connectionStatus === 'checking'
                        ? 'bg-amber-400 animate-pulse'
                        : 'bg-slate-600'
                    }`}
                  />
                  <span
                    data-testid="connection-status-text"
                    className={`text-xs tracking-wide ${
                      connectionStatus === 'connected'
                        ? 'text-emerald-400'
                        : connectionStatus === 'disconnected'
                        ? 'text-red-400'
                        : 'text-slate-500'
                    }`}
                  >
                    {connectionStatus === 'connected'
                      ? 'Server Connected'
                      : connectionStatus === 'disconnected'
                      ? 'Server Offline'
                      : connectionStatus === 'checking'
                      ? 'Checking...'
                      : 'Unknown'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={checkConnection}
                disabled={connectionStatus === 'checking'}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-40"
                data-testid="check-connection-btn"
              >
                Check Connection
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
